import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

export default function ConventionClient() {
  const { token } = useParams();
  const [data, setData]     = useState(null);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(true);
  const [phase, setPhase]   = useState('view'); // view | done
  const [form, setForm]     = useState({ signataire_nom: '', signataire_tel: '' });
  const [signing, setSigning] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/convention-public/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.error) setError(d.error);
        else {
          setData(d);
          if (d.convention.statut === 'Validée') setPhase('done');
          // Charger le HTML dans un blob pour l'iframe
          const blob = new Blob([d.html], { type: 'text/html' });
          setBlobUrl(URL.createObjectURL(blob));
        }
      })
      .catch(() => setError('Impossible de charger la convention.'))
      .finally(() => setLoading(false));
  }, [token]);

  function telPortableValide(tel) {
    const cleaned = tel.replace(/[\s.\-()]/g, '');
    return /^0[67]\d{8}$/.test(cleaned) || /^(\+|00)(\d{1,3})[67]\d{7,9}$/.test(cleaned);
  }

  async function handleSign() {
    if (!form.signataire_nom.trim()) return alert('Veuillez saisir votre nom');
    if (!form.signataire_tel.trim()) return alert('Veuillez saisir votre numéro de portable');
    if (!telPortableValide(form.signataire_tel)) return alert('Numéro de portable invalide (ex : 06 12 34 56 78 ou +33 6 12 34 56 78)');
    setSigning(true);
    try {
      const r = await fetch(`${API_BASE}/convention-public/${token}/signer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setPhase('done');
    } catch (e) { alert(e.message); }
    finally { setSigning(false); }
  }

  async function telechargerPDF() {
    const r = await fetch(`${API_BASE}/convention-public/${token}/pdf`);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `convention_${data?.convention?.convention_id || ''}.pdf`;
    a.click(); URL.revokeObjectURL(url);
  }

  const s = {
    page: { minHeight: '100vh', background: '#f0f4f8', fontFamily: 'Arial, sans-serif' },
    header: { background: '#1a3668', color: '#fff', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    headerTitle: { fontSize: '1.1rem', fontWeight: 700 },
    headerSub: { fontSize: '0.85rem', opacity: 0.8 },
    body: { maxWidth: '900px', margin: '0 auto', padding: '1.5rem 1rem' },
    card: { background: '#fff', borderRadius: '10px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden', marginBottom: '1rem' },
    iframe: { width: '100%', height: '70vh', border: 'none', display: 'block' },
    actionBar: { padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' },
    btnPrimary: { padding: '0.6rem 1.4rem', background: '#1a3668', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' },
    btnSecondary: { padding: '0.6rem 1.2rem', background: '#fff', color: '#1a3668', border: '1px solid #1a3668', borderRadius: '8px', fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' },
    signBox: { padding: '1.5rem' },
    input: { width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.95rem', marginBottom: '0.75rem' },
    label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: '0.3rem' },
    successBox: { padding: '2rem', textAlign: 'center' },
    successIcon: { fontSize: '3rem', marginBottom: '0.75rem' },
    successTitle: { fontSize: '1.3rem', fontWeight: 700, color: '#16a34a', marginBottom: '0.5rem' },
    successText: { color: '#6b7280', fontSize: '0.95rem' },
  };

  if (loading) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#1a3668' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
        <div>Chargement de la convention...</div>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...s.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '10px', padding: '2rem', textAlign: 'center', maxWidth: '400px' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
        <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: '#dc2626' }}>Lien invalide ou expiré</div>
        <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{error}</div>
      </div>
    </div>
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <div style={s.headerTitle}>📋 Convention de Formation</div>
          {data.devis && (
            <div style={s.headerSub}>{data.devis.intitule_formation} — {data.devis.client_name}</div>
          )}
        </div>
        <button onClick={telechargerPDF} style={{ ...s.btnSecondary, background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
          ⬇ Télécharger PDF
        </button>
      </div>

      <div style={s.body}>
        {/* Convention HTML */}
        {phase !== 'done' && (
          <>
            <div style={s.card}>
              {blobUrl && <iframe src={blobUrl} style={s.iframe} title="Convention de formation" />}
              <div style={s.actionBar}>
                <button style={s.btnSecondary} onClick={telechargerPDF}>⬇ Télécharger PDF</button>
              </div>
            </div>

            {/* Formulaire de signature — toujours visible si non signée */}
            {data.convention.statut !== 'Validée' && (
              <div style={s.card}>
                <div style={s.signBox}>
                  <h3 style={{ marginBottom: '0.5rem', color: '#1a3668' }}>✍️ Accord du client</h3>
                  <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.25rem' }}>
                    En signant, vous acceptez les termes de la convention de formation ci-dessus.
                  </p>
                  <label style={s.label}>Nom et prénom du signataire *</label>
                  <input style={s.input} placeholder="Ex : Jean DUPONT, Directeur"
                    value={form.signataire_nom}
                    onChange={e => setForm(f => ({ ...f, signataire_nom: e.target.value }))} />
                  <label style={s.label}>Téléphone portable *</label>
                  <input style={s.input} placeholder="Ex : 06 12 34 56 78" type="tel"
                    value={form.signataire_tel}
                    onChange={e => setForm(f => ({ ...f, signataire_tel: e.target.value }))} />
                  <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                    <button style={s.btnPrimary} onClick={handleSign} disabled={signing}>
                      {signing ? 'Signature en cours...' : '✅ Confirmer la signature'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Confirmation */}
        {phase === 'done' && (
          <div style={s.card}>
            <div style={s.successBox}>
              <div style={s.successIcon}>✅</div>
              <div style={s.successTitle}>Convention signée avec succès !</div>
              <div style={s.successText}>
                {data.convention.signataire_nom
                  ? `Signée par ${data.convention.signataire_nom}`
                  : 'Votre signature a été enregistrée.'}
              </div>
              <div style={{ marginTop: '1rem' }}>
                <button style={s.btnSecondary} onClick={telechargerPDF}>⬇ Télécharger la convention signée</button>
              </div>
            </div>
            {blobUrl && (
              <div>
                <iframe src={blobUrl} style={{ ...s.iframe, height: '60vh' }} title="Convention signée" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
